import { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';

import { supabase } from '@/integrations/supabase/client';
import { SUPABASE_URL } from '@/integrations/supabase/client';
import type { NavigatorPhase } from './PhaseIndicator';
import { toast } from 'sonner';
import {
  clearGuestNavigatorConversation,
  getGuestNavigatorSessionId,
  loadGuestNavigatorConversation,
  saveGuestNavigatorConversation,
  type NavigatorUiState,
} from './navigatorConversationStorage';
import { loadBiometricProfile } from './biometricProfileStorage';

const SERVICE_INTENT_PATTERN = /\b(find (a )?(therapist|practitioner|healer)|book|booking|appointment|see practitioners?|recommended practitioners?)\b|找(疗愈师|咨询师|服务)|预约|推荐(服务|疗愈师|咨询师)/i;

// ===== Structured Report Data Types =====

export interface ConsciousnessScores {
  O1: number;
  O2: number;
  O3: number;
  O4: number;
}

export interface ConsciousnessAnalysis {
  scores: ConsciousnessScores;
  summary: string;
  chataStage?: number;
}

export interface RootCauseFindings {
  primaryDomain: 'body' | 'vitality' | 'consciousness' | 'context';
  findings: {
    body: string;
    vitality: string;
    consciousness: string;
    context: string;
  };
}

export interface TherapyRecommendation {
  name_en: string;
  name_zh: string;
  domain: string;
  chata_stage: number;
  reason: string;
  matched_practitioners?: MatchedPractitioner[];
}

export interface MatchedPractitioner {
  name: string;
  slug: string;
  rating?: number;
  languages?: string[];
  formats?: string[];
  service_name?: string;
  price?: number;
  currency?: string;
  duration_minutes?: number;
}

export interface TherapyPlan {
  recommendations: TherapyRecommendation[];
}

export interface ReportData {
  consciousnessAnalysis?: ConsciousnessAnalysis;
  rootCauseFindings?: RootCauseFindings;
  therapyPlan?: TherapyPlan;
}

// Compute CHATA stage from average O scores
function computeChataStage(scores: ConsciousnessScores): number {
  const avg = (scores.O1 + scores.O2 + scores.O3 + scores.O4) / 4;
  if (avg <= 2) return 0;
  if (avg <= 4) return 1;
  if (avg <= 6) return 2;
  if (avg <= 8) return 3;
  return 4;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const defaultUiState = (): NavigatorUiState => ({
  clickedServiceLinks: [],
  quickReplies: [],
  detectedStage: null,
  branchPromptShownAt: null,
  branchChoice: null,
});

export function useNavigatorChat() {
  const { user, activeRole } = useAuth();
  // AI Navigator conversations FK to `clients(id)`. Non-client roles (practitioner/host/admin)
  // have no clients row, so persist to guest storage instead of attempting a DB insert.
  const isClientUser = !!user?.id && activeRole === 'client';
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // N-2: live status line ("Looking up therapies…") pushed by the server before
  // each tool call, so the user never faces a silent screen.
  const [statusText, setStatusText] = useState<string | null>(null);
  // N-6: set when a therapy/nutrient lookup failed — recommendations may be incomplete.
  const [degraded, setDegraded] = useState(false);

  const [phase, setPhase] = useState<NavigatorPhase>('reception');
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [recommendations, setRecommendations] = useState<TherapyRecommendation[]>([]);
  const [reportData, setReportData] = useState<ReportData>({});
  const [uiState, setUiState] = useState<NavigatorUiState>(defaultUiState);
  const guestSessionIdRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Refs to track latest values for saving (avoids stale closure issues)
  const reportDataRef = useRef<ReportData>({});
  const recommendationsRef = useRef<TherapyRecommendation[]>([]);
  const [dailyUsed, setDailyUsed] = useState<number>(0);
  const [paidBalance, setPaidBalance] = useState<number>(0);

  // Pending guest conversation awaiting user's choice (Continue / Start fresh)
  const [pendingGuestResume, setPendingGuestResume] = useState<{
    conversation: ReturnType<typeof loadGuestNavigatorConversation>;
    userTurns: number;
    lastUpdatedAt: string;
    reachedBranch: boolean;
  } | null>(null);

  // Fetch daily usage and paid balance on mount
  const fetchUsageData = useCallback(async () => {
    if (!user?.id) return;
    const today = new Date().toISOString().split('T')[0];
    const [usageRes, balanceRes] = await Promise.all([
      supabase.from('ai_daily_message_usage' as any).select('message_count').eq('user_id', user.id).eq('message_date', today).maybeSingle(),
      supabase.from('ai_message_balances' as any).select('balance').eq('user_id', user.id).maybeSingle(),
    ]);
    setDailyUsed((usageRes.data as any)?.message_count ?? 0);
    setPaidBalance((balanceRes.data as any)?.balance ?? 0);
  }, [user?.id]);

  // Auto-fetch on mount
  useEffect(() => { fetchUsageData(); }, [fetchUsageData]);

  const applyGuestConversation = useCallback((guestConversation: NonNullable<ReturnType<typeof loadGuestNavigatorConversation>>) => {
    setMessages(guestConversation.messages);
    setPhase(guestConversation.phase || 'reception');
    setReportData(guestConversation.reportData || {});
    reportDataRef.current = guestConversation.reportData || {};
    setRecommendations(guestConversation.recommendations || []);
    recommendationsRef.current = guestConversation.recommendations || [];
    // Never restore stale quick-reply chips — they must regenerate from the
    // current assistant turn + current UI language to avoid cross-session/language leak.
    const restoredUi = guestConversation.uiState || defaultUiState();
    setUiState({ ...restoredUi, quickReplies: [] });
  }, []);

  // Guest session recovery: do NOT auto-restore. Show a prompt unless auto-rules apply.
  useEffect(() => {
    if (user?.id) return;
    const guestConversation = loadGuestNavigatorConversation();
    if (!guestConversation?.messages?.length) return;

    const userTurns = guestConversation.messages.filter((m) => m.role === 'user').length;
    const updatedAt = guestConversation.updatedAt || new Date().toISOString();
    const ageMs = Date.now() - new Date(updatedAt).getTime();
    const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
    const reachedBranch = Boolean(
      guestConversation.uiState?.branchPromptShownAt ||
      guestConversation.uiState?.branchChoice ||
      (guestConversation.recommendations && guestConversation.recommendations.length > 0)
    );

    // Auto rule: too short or too stale → start fresh silently
    if ((userTurns < 2 && !reachedBranch) || ageMs > TWENTY_FOUR_HOURS) {
      clearGuestNavigatorConversation();
      return;
    }

    // Otherwise, prompt the user to choose
    setPendingGuestResume({
      conversation: guestConversation,
      userTurns,
      lastUpdatedAt: updatedAt,
      reachedBranch,
    });
  }, [user?.id]);

  const resumeGuestConversation = useCallback(() => {
    if (!pendingGuestResume?.conversation) {
      setPendingGuestResume(null);
      return;
    }
    applyGuestConversation(pendingGuestResume.conversation);
    setPendingGuestResume(null);
  }, [pendingGuestResume, applyGuestConversation]);

  const dismissGuestResume = useCallback(() => {
    clearGuestNavigatorConversation();
    setPendingGuestResume(null);
  }, []);

  useEffect(() => {
    if (user?.id || messages.length === 0) return;
    saveGuestNavigatorConversation({
      messages,
      phase,
      reportData,
      recommendations,
      uiState,
      updatedAt: new Date().toISOString(),
    });
  }, [messages, phase, reportData, recommendations, uiState, user?.id]);

  // Auto-restore latest conversation on mount; migrate homepage guest conversation after login first.
  const hasAutoLoaded = useRef(false);
  useEffect(() => {
    if (!isClientUser || hasAutoLoaded.current) return;
    hasAutoLoaded.current = true;
    const loadLatest = async () => {
      try {
        const guestConversation = loadGuestNavigatorConversation();
        if (guestConversation?.messages?.length) {
          const analysisResults = {
            reportData: guestConversation.reportData || {},
            recommendations: guestConversation.recommendations || [],
            uiState: guestConversation.uiState || defaultUiState(),
          };
          const title = guestConversation.messages[0]?.content?.slice(0, 50) || 'Homepage conversation';
          const { data: migrated, error: migrateError } = await supabase.functions.invoke('merge-conversation', {
            body: {
              guest_session_id: guestConversation.guestSessionId || getGuestNavigatorSessionId(),
              title,
              messages: guestConversation.messages,
              phase: guestConversation.phase || 'reception',
              analysis_results: analysisResults,
            },
          });

          if (!migrateError && migrated) {
            clearGuestNavigatorConversation();
            loadConversation((migrated as any).conversation_id);
            return;
          }
          console.warn('Failed to migrate guest conversation:', migrateError);
        }

        const { data } = await supabase
          .from('ai_navigator_conversations' as any)
          .select('id')
          .eq('user_id', user.id)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (data) loadConversation((data as any).id);
      } catch (err) {
        console.warn('Failed to auto-load latest conversation:', err);
      }
    };
    loadLatest();
  }, [user?.id, isClientUser]);

  // Handle structured metadata from SSE events (replaces regex extraction)
  const handleMetadata = useCallback((metadata: any) => {
    if (metadata.crisis_detected) {
      setPhase('crisis');
      return;
    }

    if (metadata.phase) {
      setPhase(metadata.phase as NavigatorPhase);
    }

    if (metadata.report_update) {
      const update = metadata.report_update;

      if (update.type === 'consciousness_analysis' && update.data) {
        const scores = update.data.scores as ConsciousnessScores;
        const chataStage = computeChataStage(scores);
        const newData = {
          consciousnessAnalysis: {
            scores,
            summary: update.data.summary || '',
            chataStage,
          },
        };
        setReportData(prev => {
          const updated = { ...prev, ...newData };
          reportDataRef.current = updated;
          return updated;
        });
        toast.info('🧠 意识分析已更新 / Awareness analysis updated');
      }

      if (update.type === 'root_cause_analysis' && update.data) {
        const newData = {
          rootCauseFindings: {
            primaryDomain: update.data.primary_domain,
            findings: update.data.findings,
          },
        };
        setReportData(prev => {
          const updated = { ...prev, ...newData };
          reportDataRef.current = updated;
          return updated;
        });
        toast.info('🔍 溯因分析已更新 / Root cause analysis updated');
      }

      if (update.type === 'therapy_recommendations' && update.data) {
        const recs = update.data.recommendations || [];
        setRecommendations(recs);
        recommendationsRef.current = recs;
        setReportData(prev => {
          const updated = { ...prev, therapyPlan: { recommendations: recs } };
          reportDataRef.current = updated;
          return updated;
        });
        toast.info('🗺️ 推荐路径已更新 / Therapy plan updated');
      }
    }
  }, []);

  const saveConversation = useCallback(async (msgs: ChatMessage[], currentPhase: NavigatorPhase, currentReportData?: ReportData, currentRecommendations?: TherapyRecommendation[], currentUiState?: NavigatorUiState) => {
    if (!isClientUser) {
      const guestSessionId = guestSessionIdRef.current || getGuestNavigatorSessionId();
      guestSessionIdRef.current = guestSessionId;
      saveGuestNavigatorConversation({
        guestSessionId,
        messages: msgs,
        phase: currentPhase,
        reportData: currentReportData || reportData,
        recommendations: currentRecommendations || recommendations,
        uiState: currentUiState || uiState,
        updatedAt: new Date().toISOString(),
      });
      await supabase.functions.invoke('merge-conversation', {
        body: {
          action: 'save_guest',
          guest_session_id: guestSessionId,
          messages: msgs,
          phase: currentPhase,
          analysis_results: {
            reportData: currentReportData || reportData,
            recommendations: currentRecommendations || recommendations,
            uiState: currentUiState || uiState,
          },
        },
      });
      return;
    }
    try {
      const messagesJson = msgs.map(m => ({
        role: m.role,
        content: m.content,
        timestamp: new Date().toISOString(),
        phase: currentPhase,
      }));

      const analysisResults = {
        reportData: currentReportData || reportData,
        recommendations: currentRecommendations || recommendations,
        uiState: currentUiState || uiState,
      };

      if (conversationId) {
        const { error } = await supabase
          .from('ai_navigator_conversations' as any)
          .update({
            messages: messagesJson,
            current_phase: currentPhase,
            analysis_results: analysisResults,
            updated_at: new Date().toISOString(),
          } as any)
          .eq('id', conversationId);
        if (error) console.error('Failed to update conversation:', error);
      } else {
        const title = msgs[0]?.content?.slice(0, 50) || 'New conversation';
        const { data, error } = await supabase
          .from('ai_navigator_conversations' as any)
          .insert({
            user_id: user.id,
            title,
            messages: messagesJson,
            current_phase: currentPhase,
            analysis_results: analysisResults,
          } as any)
          .select('id')
          .single();
        if (error) console.error('Failed to insert conversation:', error);
        if (data) setConversationId((data as any).id);
      }
    } catch (err) {
      console.error('Failed to save conversation:', err);
    }
  }, [user?.id, isClientUser, conversationId, reportData, recommendations, uiState]);

  const sendMessage = useCallback(async (input: string, language: string) => {
    if (!input.trim() || isLoading) return;

    const userMsg: ChatMessage = { role: 'user', content: input };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setIsLoading(true);
    setStatusText(null);
    setDegraded(false);


    abortRef.current = new AbortController();

    let assistantContent = '';
    let latestPhase = phase;
    let streamError: string | null = null;
    let streamErrorCode: number | undefined;


    try {
      // Refresh session to avoid stale tokens for authenticated dashboard usage.
      if (user?.id) await supabase.auth.refreshSession();

      const makeRequest = async () => {
        const session = user?.id ? (await supabase.auth.getSession()).data.session : null;
        return fetch(`${SUPABASE_URL}/functions/v1/ai-navigator`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token || ''}`,
          },
          body: JSON.stringify({
            messages: newMessages.map(m => ({ role: m.role, content: m.content })),
            phase,
            conversation_id: conversationId,
            guest_session_id: user?.id ? undefined : (guestSessionIdRef.current || getGuestNavigatorSessionId()),
            ai_mode: SERVICE_INTENT_PATTERN?.test?.(input) ? 'recommendation' : 'exploration',
            language,
            biometrics: loadBiometricProfile() ?? undefined,
          }),
          signal: abortRef.current.signal,
        });
      };

      let resp = await makeRequest();

      // Retry once on 401 after forcing a session refresh
      if (resp.status === 401) {
        console.log('Got 401, retrying after session refresh...');
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (!refreshError) {
          resp = await makeRequest();
        }
      }

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        if (resp.status === 429) {
          throw new Error('请求过于频繁，请稍后再试 / Rate limit exceeded, please try again later.');
        }
        if (resp.status === 402) {
          toast.error('每日免费消息已用完 / Daily free messages used up', {
            description: '请前往 Payment 页面购买更多消息 / Go to Payment to purchase more messages',
            action: { label: 'Go to Payment', onClick: () => window.location.href = '/client-dashboard?tab=payment' },
            duration: 10000,
          });
          throw new Error('每日免费消息额度已用完，请购买更多消息。/ Daily free limit reached. Purchase messages to continue.');
        }
        throw new Error(errorData.error || `Error ${resp.status}`);
      }

      if (!resp.body) throw new Error('No response body');

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = '';

      const processLine = (line: string) => {
        if (line.endsWith('\r')) line = line.slice(0, -1);
        if (line.startsWith(':') || line.trim() === '') return;
        if (!line.startsWith('data: ')) return;

        const jsonStr = line.slice(6).trim();
        if (jsonStr === '[DONE]') return;

        try {
          const parsed = JSON.parse(jsonStr);

          // N-2: live status line (never enters the message history)
          if (parsed.status) {
            setStatusText(parsed.status.text || null);
            return;
          }

          // In-stream errors (rate limit / credits / gateway failure)
          if (parsed.error) {
            streamError = parsed.error;
            streamErrorCode = parsed.code;
            return;
          }

          // Handle metadata events (from agentic tool calls)
          if (parsed.metadata) {
            if (parsed.metadata.degraded) setDegraded(true);
            handleMetadata(parsed.metadata);
            if (parsed.metadata.phase) latestPhase = parsed.metadata.phase;
            return; // metadata-only events have no content
          }

          // Handle content delta
          const content = parsed.choices?.[0]?.delta?.content as string | undefined;
          if (content) {
            setStatusText(null);
            assistantContent += content;
            setMessages(prev => {
              const last = prev[prev.length - 1];
              if (last?.role === 'assistant') {
                return prev.map((m, i) => i === prev.length - 1 ? { ...m, content: assistantContent } : m);
              }
              return [...prev, { role: 'assistant', content: assistantContent }];
            });
          }

        } catch (parseErr) {
          // Skip malformed SSE line (do NOT put back — causes infinite loop)
          console.warn('Skipping malformed SSE data:', jsonStr);
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf('\n')) !== -1) {
          const line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);
          processLine(line);
        }
      }

      // Final flush
      if (textBuffer.trim()) {
        for (const raw of textBuffer.split('\n')) {
          if (raw) processLine(raw);
        }
      }

      // In-stream failures surface as real errors (quota / rate limit / gateway)
      if (streamError && !assistantContent) {
        if (streamErrorCode === 402) {
          toast.error('每日免费消息已用完 / Daily free messages used up', {
            description: '请前往 Payment 页面购买更多消息 / Go to Payment to purchase more messages',
            action: { label: 'Go to Payment', onClick: () => window.location.href = '/client-dashboard?tab=payment' },
            duration: 10000,
          });
        }
        throw new Error(
          streamErrorCode === 429
            ? '请求过于频繁，请稍后再试 / Rate limit exceeded, please try again later.'
            : streamError
        );
      }

      // Save conversation with latest report data from refs (avoids stale state)
      const finalMessages = [...newMessages, { role: 'assistant' as const, content: assistantContent }];
       await saveConversation(finalMessages, latestPhase as NavigatorPhase, reportDataRef.current, recommendationsRef.current, uiState);


    } catch (e: any) {
      if (e.name === 'AbortError') return;
      console.error('Navigator chat error:', e);
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: `⚠️ ${e.message || 'Something went wrong. Please try again.'}` },
      ]);
    } finally {
      setIsLoading(false);
      setStatusText(null);
      fetchUsageData(); // Refresh usage counts after each message

    }
  }, [messages, isLoading, phase, conversationId, handleMetadata, saveConversation, user?.id, uiState]);

  const startNewConversation = useCallback(() => {
    setMessages([]);
    setPhase('reception');
    setConversationId(null);
    setRecommendations([]);
    setReportData({});
    setUiState(defaultUiState());
    if (!user?.id) clearGuestNavigatorConversation();
    // Reset refs too
    reportDataRef.current = {};
    recommendationsRef.current = [];
  }, [user?.id]);

  const loadConversation = useCallback(async (convId: string) => {
    try {
      const { data, error } = await supabase
        .from('ai_navigator_conversations' as any)
        .select('*')
        .eq('id', convId)
        .single();

      if (error) {
        console.error('Error loading conversation:', error);
        toast.error('加载对话失败 / Failed to load conversation');
        return;
      }

      if (data) {
        const conv = data as any;
        setConversationId(conv.id);
        setPhase(conv.current_phase || 'reception');
        
        // Safely parse messages with validation
        const rawMessages = conv.messages || [];
        const msgs: ChatMessage[] = [];
        for (const m of rawMessages) {
          if (m && typeof m.content === 'string' && (m.role === 'user' || m.role === 'assistant')) {
            msgs.push({ role: m.role, content: m.content });
          }
        }
        setMessages(msgs);

        // Restore report data and recommendations from analysis_results
        const analysisResults = conv.analysis_results;
        if (analysisResults) {
          if (analysisResults.reportData) {
            setReportData(analysisResults.reportData);
            reportDataRef.current = analysisResults.reportData;
          } else {
            setReportData({});
            reportDataRef.current = {};
          }
          if (analysisResults.recommendations && Array.isArray(analysisResults.recommendations)) {
            setRecommendations(analysisResults.recommendations);
            recommendationsRef.current = analysisResults.recommendations;
          } else {
            setRecommendations([]);
            recommendationsRef.current = [];
          }
          // Never restore stale quick-reply chips on conversation load.
          const restoredUi = analysisResults.uiState || defaultUiState();
          setUiState({ ...restoredUi, quickReplies: [] });
        } else {
          // No analysis results - reset to empty
          setReportData({});
          setRecommendations([]);
          reportDataRef.current = {};
          recommendationsRef.current = [];
          setUiState(defaultUiState());
        }
      }
    } catch (err) {
      console.error('Error loading conversation:', err);
      toast.error('加载对话失败 / Failed to load conversation');
    }
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  /**
   * Append a synthetic assistant message into the conversation.
   * Used to write back results from Deep Mode (the /framework consciousness
   * engine) so the user perceives the deep analysis as a continuation of the
   * SAME Reso conversation rather than a separate tool's output.
   */
  const appendAssistantMessage = useCallback((content: string) => {
    if (!content?.trim()) return;
    setMessages(prev => {
      // De-duplicate against the most recent assistant turn.
      const last = prev[prev.length - 1];
      if (last?.role === 'assistant' && last.content === content) return prev;
      return [...prev, { role: 'assistant', content }];
    });
  }, []);

  return {
    messages,
    isLoading,
    statusText,
    degraded,

    phase,
    recommendations,
    reportData,
    conversationId,
    uiState,
    setUiState,
    sendMessage,
    startNewConversation,
    loadConversation,
    cancel,
    dailyUsed,
    paidBalance,
    freeRemaining: Math.max(0, 50 - dailyUsed),
    appendAssistantMessage,
    // Guest session recovery
    pendingGuestResume,
    resumeGuestConversation,
    dismissGuestResume,
  };
}

